import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Modal from "@/components/Modal";
import {
  fetchSlideHtml,
  prefetchSlideHtmlAssets,
  withBaseHref,
} from "@/features/tutor/utils/slideHtmlLoader";
import {
  isAssessmentSlideType,
  isPracticeSlideType,
} from "@/features/tutor/constants/assessmentTypes";
import AssessmentPreviewSurvey from "./AssessmentPreviewSurvey";
import PracticeDiagramFrame from "@/components/practiceDiagram/PracticeDiagramFrame";
import { usePracticeDiagram } from "@/components/practiceDiagram/practiceDiagramLoader";
import { SLIDE_HEIGHT, SLIDE_WIDTH } from "@/constants/slideDeck";
import { useFitScale } from "@/hooks/useFitScale";
import { PreviewFetchStatus, rowPreviewState } from "./lessonRows";

export interface PreviewSlide {
  name: string | null;
  slide_html: string | null;
  slide_index: number;
  slide_type?: string | null;
  survey_json?: Record<string, unknown> | null;
  // Practice bites only: the unpublished host slide's diagram html.
  practice_host_html?: string | null;
}

interface SlideHtmlPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonName: string;
  slides: PreviewSlide[];
  surveysStatus?: PreviewFetchStatus;
  onRetrySurveys?: () => void;
  initialIndex: number;
  onAssign?: () => void;
}

const SlideHtmlPreviewModal = ({
  isOpen,
  onClose,
  lessonName,
  slides,
  surveysStatus = "loaded",
  onRetrySurveys,
  initialIndex,
  onAssign,
}: SlideHtmlPreviewModalProps) => {
  const [current, setCurrent] = useState(initialIndex);
  const [htmlCache, setHtmlCache] = useState<Record<number, string>>({});
  const stageRef = useRef<HTMLDivElement>(null);

  const safeIndex = slides.length
    ? Math.min(Math.max(current, 0), slides.length - 1)
    : 0;
  const slide = slides[safeIndex];
  const isAssessment = isAssessmentSlideType(slide?.slide_type);
  const surveyState = rowPreviewState(!!slide?.survey_json, surveysStatus);
  const isPractice = isPracticeSlideType(slide?.slide_type);
  const [practiceDiagram, retryPracticeDiagram] = usePracticeDiagram(
    slide?.practice_host_html,
    isOpen && isPractice,
  );

  useEffect(() => {
    if (isOpen) setCurrent(initialIndex);
  }, [isOpen, initialIndex]);

  // Prefetch every slide's raw HTML once the modal opens so navigation is
  // instant and the iframe renders from srcDoc rather than a slow network src.
  useEffect(() => {
    if (!isOpen || slides.length === 0) return;
    const controller = new AbortController();

    const load = (slide: PreviewSlide) => {
      if (!slide.slide_html || isAssessmentSlideType(slide.slide_type)) return;
      fetchSlideHtml(slide.slide_html, controller.signal)
        .then((raw) => {
          prefetchSlideHtmlAssets(raw, slide.slide_html as string);
          setHtmlCache((prev) =>
            prev[slide.slide_index] !== undefined
              ? prev
              : {
                  ...prev,
                  [slide.slide_index]: withBaseHref(
                    raw,
                    slide.slide_html as string,
                  ),
                },
          );
        })
        .catch(() => {
          // Best-effort; the "loading" state stays until a retry succeeds.
        });
    };

    slides.forEach(load);

    return () => controller.abort();
  }, [isOpen, slides]);

  // Scale the fixed-size slide stage down to whatever width the modal gives us.
  // The stage is swapped out for assessments, hence the remount on that.
  const scale = useFitScale(stageRef, { width: SLIDE_WIDTH }, isOpen, 0.45, [
    isAssessment,
  ]);

  const isLoadingSlides = isOpen && slides.length === 0;

  if (!isOpen && slides.length === 0) return null;

  const html = slide ? htmlCache[slide.slide_index] : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="!w-[92vw] !max-w-[560px] md:!w-[560px] max-h-[88vh] rounded-[20px] p-6"
    >
      {isLoadingSlides ? (
        <div className="flex h-[320px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E0E0E0] border-t-[#28543D]" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-0.5 pr-8">
            {slide.name && (
              <span className="font-mulish font-bold text-[#28543D] text-xs">
                {slide.name}
              </span>
            )}
            <h2 className="font-mulish font-bold text-xl text-[#294B39]">
              {lessonName}
            </h2>
          </div>

          {isAssessment ? (
            <div className="relative w-full min-h-[200px] max-h-[60vh] overflow-y-auto rounded-[16px] border border-[#EAEAEA] bg-white shadow-[0px_4px_20px_rgba(0,0,0,0.06)]">
              {surveyState === "ready" && (
                <AssessmentPreviewSurvey
                  key={slide.slide_index}
                  surveyJson={slide.survey_json as Record<string, unknown>}
                  singlePage
                />
              )}
              {surveyState === "loading" && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-7 h-7 border-2 border-[#E0E0E0] border-t-[#28543D] rounded-full animate-spin" />
                </div>
              )}
              {surveyState === "error" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
                  <span className="text-[#B4453C]">
                    Couldn&rsquo;t load this page.
                  </span>
                  <span className="text-sm text-[#25252599]">
                    This is a loading problem, not an empty page.
                  </span>
                  {onRetrySurveys && (
                    <button
                      type="button"
                      onClick={onRetrySurveys}
                      className="font-mulish font-bold text-[#28543D] underline hover:opacity-80"
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              ref={stageRef}
              className="relative w-full rounded-[16px] border border-[#EAEAEA] overflow-hidden bg-white shadow-[0px_4px_20px_rgba(0,0,0,0.06)]"
              style={{ height: SLIDE_HEIGHT * scale }}
            >
              {isPractice ? (
                <>
                  {practiceDiagram.status === "loading" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white">
                      <div className="w-7 h-7 border-2 border-[#E0E0E0] border-t-[#28543D] rounded-full animate-spin" />
                    </div>
                  )}
                  {practiceDiagram.status === "ready" && (
                    <PracticeDiagramFrame
                      key={slide.slide_index}
                      diagram={practiceDiagram.diagram}
                      scale={scale}
                      title={slide.name || `Slide ${safeIndex + 1}`}
                    />
                  )}
                  {practiceDiagram.status === "missing" && (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      No diagram for this practice.
                    </div>
                  )}
                  {practiceDiagram.status === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
                      <span className="text-sm text-[#B4453C]">
                        Couldn&rsquo;t load this diagram.
                      </span>
                      <button
                        type="button"
                        onClick={retryPracticeDiagram}
                        className="text-sm font-bold text-[#28543D] underline hover:opacity-80"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                </>
              ) : slide.slide_html ? (
                <>
                  {html === undefined && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white">
                      <div className="w-7 h-7 border-2 border-[#E0E0E0] border-t-[#28543D] rounded-full animate-spin" />
                    </div>
                  )}
                  {html !== undefined && (
                    <iframe
                      key={slide.slide_index}
                      srcDoc={html}
                      title={slide.name || `Slide ${safeIndex + 1}`}
                      className="border-0"
                      scrolling="no"
                      sandbox="allow-scripts allow-same-origin"
                      style={{
                        width: SLIDE_WIDTH,
                        height: SLIDE_HEIGHT,
                        transform: `scale(${scale})`,
                        transformOrigin: "top left",
                      }}
                    />
                  )}
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                  No preview available for this slide.
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              aria-label="Previous slide"
              disabled={safeIndex === 0}
              onClick={() => setCurrent((i) => Math.max(0, i - 1))}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-[#E8E8E8] text-[#252525] hover:bg-[#F8F8F8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-[#25252599] font-medium">
              Page {safeIndex + 1} of {slides.length}
            </span>
            <button
              type="button"
              aria-label="Next slide"
              disabled={safeIndex === slides.length - 1}
              onClick={() =>
                setCurrent((i) => Math.min(slides.length - 1, i + 1))
              }
              className="w-10 h-10 flex items-center justify-center rounded-full border border-[#E8E8E8] text-[#252525] hover:bg-[#F8F8F8] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="font-mulish font-bold text-[#252525] px-4 py-2 hover:opacity-80 transition-opacity"
            >
              Close
            </button>
            {onAssign && (
              <button
                type="button"
                onClick={onAssign}
                className="font-mulish font-bold rounded-xl px-6 py-2.5 bg-[#28543D] text-white hover:opacity-90 transition-opacity"
              >
                Assign to a group
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SlideHtmlPreviewModal;
